import apiClient from "./client";
import type { ApiErrorResponse } from "./events";

export type ShortLink = {
  uuid: string;
  code: string;
  targetUrl: string;
  visits: number;
  lastVisitedAt?: string | null;
  active: boolean;
  createdAt: string;
  shortUrl: string;
  /** JUB-51: the guest the link was created for; null for an event-wide link. */
  guestUuid?: string | null;
};

// Success bodies come either bare or as { message_key, data } (the client does
// not unwrap them), so read the payload from whichever shape arrived.
function unwrap<T>(body: unknown): T {
  if (body && typeof body === "object" && !Array.isArray(body) && "data" in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

export async function getEventShortLinks(eventUuid: string): Promise<ShortLink[]> {
  const response = await apiClient.get<ShortLink[] | ApiErrorResponse>(
    `/events/${eventUuid}/short-links`,
  );
  const data = response.data;
  if (data && typeof data === "object" && "error" in data) {
    throw new Error(data.error?.message || "Failed to load short links");
  }
  const links = unwrap<ShortLink[]>(data);
  return Array.isArray(links) ? links : [];
}

export async function createShortLink(
  eventUuid: string,
  locale: string = "ro",
  guestUuid?: string,
): Promise<ShortLink> {
  const response = await apiClient.post<ShortLink | { data: ShortLink } | ApiErrorResponse>(
    `/events/${eventUuid}/short-links`,
    guestUuid ? { locale, guestUuid } : { locale },
  );
  const data = response.data;
  if (data && typeof data === "object" && "error" in data) {
    throw new Error(data.error?.message || "Failed to create short link");
  }
  return unwrap<ShortLink>(data);
}

export async function deleteShortLink(
  eventUuid: string,
  linkUuid: string,
): Promise<void> {
  const response = await apiClient.delete<{ message?: string } | ApiErrorResponse>(
    `/events/${eventUuid}/short-links/${linkUuid}`,
  );
  const data = response.data;
  if (data && typeof data === "object" && "error" in data) {
    throw new Error(data.error?.message || "Failed to delete short link");
  }
}

